import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type { IFilterInstance } from "../../types/filterInstance";
import { collectFlatFilterDefaults } from "../../utils/collectFilterDefaults";
import { groupDynamicFilterFields, ungroupDynamicFilterFields } from "../../utils/dynamicFilterGrouping";

import type { IFilterManagerModel, IFilterManagerOptions } from "./types";

export const createFiltersManager = <T extends IPlainObject>({
    filtersConfig,
    filtersValue: rawFiltersValue,
    updateFilters: updateFiltersValue,
}: IFilterManagerOptions<T>): IFilterManagerModel<T> => {
    const filtersValue = groupDynamicFilterFields(rawFiltersValue, filtersConfig);

    // Every card asks for its config once per render (dirty + disabled state); a linear scan there makes
    // the panel O(n²) over ~50 filters on each interaction.
    const configByKey = new Map<keyof T, IFilterInstance<T>>(filtersConfig.map((config) => [config.key, config]));

    const getAll = (): readonly IFilterInstance<T>[] => {
        return filtersConfig;
    };

    const getFilterConfig = (filterKey: keyof T): IFilterInstance<T> | undefined => {
        return configByKey.get(filterKey);
    };

    const getFilterDtoValue = (filterKey: keyof T): T[keyof T] | undefined => {
        return filtersValue[filterKey];
    };

    const updateFilters = (newFilterValue: Partial<T>): void => {
        const checkedFilterValue: Partial<T> = { ...newFilterValue };

        // Every read comes from the incoming value, every write lands on the draft, and a filter only ever
        // writes its own key — so `filtersConfig` order cannot affect the outcome.
        filtersConfig.forEach(({ filter, key }) => {
            const changedFilterValue = filter.affectValue(newFilterValue, newFilterValue[key]);

            if (changedFilterValue === undefined) {
                delete checkedFilterValue[key];
            } else {
                checkedFilterValue[key] = changedFilterValue;
            }
        });

        updateFiltersValue(ungroupDynamicFilterFields(checkedFilterValue, filtersConfig));
    };

    const updateFilter = (key: keyof T, value?: T[keyof T]): void => {
        const newFiltersValue = { ...filtersValue };

        if (is.undefined(value)) {
            delete newFiltersValue[key];
        } else {
            newFiltersValue[key] = value;
        }

        updateFilters(newFiltersValue);
    };

    const resetFilter = (filterKey: keyof T): void => {
        const filter: IFilterInstance<T> | undefined = configByKey.get(filterKey);

        if (!filter) {
            return;
        }

        updateFilter(filterKey, filter.filter.getEmptyValue());
    };

    const resetAllFilters = (): void => {
        updateFilters(collectFlatFilterDefaults<T>(getAll()));
    };

    const isFilterDirty = (filterKey: keyof T): boolean => {
        const filterConfig = getFilterConfig(filterKey);

        if (!filterConfig) {
            return false;
        }

        return filterConfig.filter.isFilterDirty(filtersValue[filterKey]);
    };

    const isFilterDisabled = (filterKey: keyof T): boolean =>
        getFilterConfig(filterKey)?.filter.isDisabled(filtersValue) ?? false;

    const getDirtyFilters = (): Array<keyof T> =>
        filtersConfig.filter((config) => config.filter.isFilterDirty(filtersValue[config.key])).map(({ key }) => key);

    const normalizeAndUpdateFilter = (key: keyof T, value?: T[keyof T]): void => {
        const filterConfig = getFilterConfig(key);

        if (!filterConfig) {
            return;
        }

        const apiValue = filterConfig.filter.toApiValue(value);

        if (filterConfig.filter.isValueEmpty(apiValue)) {
            updateFilter(key, undefined);
        } else {
            updateFilter(key, apiValue);
        }
    };

    return {
        getAll,
        resetFilter,
        resetAllFilters,
        isFilterDirty,
        isFilterDisabled,
        getDirtyFilters,
        getFilterConfig,
        getFilterDtoValue,
        updateFilters,
        updateFilter,
        normalizeAndUpdateFilter,
    };
};
